import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Stepper, StepperActions, StepperContent } from '@/components/ui/stepper';
import { ArrowLeft, ArrowRight, ExternalLink, FileDown, Play, RefreshCw, Sparkles, Upload, AlertTriangle, GraduationCap } from 'lucide-react';

import {
  ALL_LOGICAL_FILES,
  CREATE_FILE_BY_DIMENSION,
  FILE_LABELS,
  FILE_TO_STEP,
  STEP_BY_ID,
  STEP_CATALOG,
  closureFilesSatisfied,
  missingCoherenceFiles,
  resolveDependencies,
  sortStepsByDependencies,
} from './stepCatalog';
import { DEFAULT_WIZARD_CONFIG, type WizardConfig } from './defaults';
import type {
  CsvDelimiter,
  CustomFieldDecisions,
  CustomFieldsImportReport,
  EngineRun,
  EngineStepReport,
  EnMappingDecisions,
  EnMappingModel,
  FieldValueJobOutput,
  FieldValueJobRef,
  NativeValuesImportReport,
  ReconciliationDecisions,
  ReconciliationModel,
  SchoolPeriod,
  TenantOverview as TenantOverviewModel,
  UpsertPreview,
} from './types';
import * as api from './api';
import {
  createImportSession,
  finishImportSession,
  generateImportReport,
  type ImportSessionSummary,
} from '@/api/import-history';
import { ImportReportView } from '@/components/import-report/ImportReportView';
import { StepSelector } from './components/StepSelector';
import { FileUploader } from './components/FileUploader';
import { ConfigForm } from './components/ConfigForm';
import { AnomaliesPanel, UpsertSection } from './components/PreviewPanel';
import { TenantOverview } from './components/TenantOverview';
import { ReconciliationStep } from './components/ReconciliationStep';
import { EducationNationaleMapping } from './components/EducationNationaleMapping';
import { CustomFieldsMapping } from './components/CustomFieldsMapping';
import { CustomFieldsReport } from './components/CustomFieldsReport';
import {
  EngineStepsPanel,
  FieldValueJobsSummary,
  NativeValuesReport,
} from './components/PostImportPanel';
import { initDecisions } from './reconciliation';
import { isConvertedWorkbookFile } from './workbookBundle';
import { detectCsvDelimiter } from './csvDelimiter';
import { initEnDecisions } from './enMapping';
import { hasCfImports, initCfDecisions } from './customFields';

type StepId = 'overview' | 'data' | 'files' | 'config' | 'reconcile' | 'run' | 'report';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Taille de page des passes post-import : dimensionnée pour tenir sous les
 * timeouts des proxys même en repli synchrone (binding par binding quand le
 * batch asynchrone V2 n'est pas disponible).
 */
const POST_PAGE_SIZE = 25;

/**
 * Taille de page de la passe valeurs natives : le géocodage des adresses fait
 * plusieurs appels V2 séquentiels PAR LIGNE (autocomplete + création +
 * binding) — 25 lignes dépassent les timeouts de l'infra en prod (connexion
 * coupée sans réponse → « Network Error »).
 */
const NATIVE_PAGE_SIZE = 10;

/** Erreur transitoire : pas de réponse HTTP (connexion coupée) ou 5xx passerelle. */
const isTransientError = (e: unknown): boolean =>
  axios.isAxiosError(e) && (!e.response || [502, 503, 504].includes(e.response.status));

/**
 * Rejoue une page en cas d'erreur transitoire (les passes d'import sont des
 * upserts : rejouer une page déjà partiellement traitée est sans effet de
 * bord). Backoff progressif pour laisser l'infra retomber.
 */
async function withRetry<T>(fn: () => Promise<T>, attempts = 4): Promise<T> {
  let delay = 2000;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (!isTransientError(e) || attempt >= attempts) throw e;
      await sleep(delay);
      delay = Math.min(delay * 2, 15000);
    }
  }
}

const fileSignature = (files: Record<string, File>) =>
  Object.entries(files)
    .map(([k, f]) => `${k}:${f.size}:${f.lastModified}`)
    .sort()
    .join('|');

export function SchoolImportV2Page() {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const [advancedMode, setAdvancedMode] = useState(false);
  const [currentId, setCurrentId] = useState<StepId>('overview');

  // Étape — contenu de l'école & année scolaire
  const [overview, setOverview] = useState<TenantOverviewModel | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [periods, setPeriods] = useState<SchoolPeriod[]>([]);
  const [periodsError, setPeriodsError] = useState<string | null>(null);
  const [targetSchoolPeriodId, setTargetSchoolPeriodId] = useState('');

  // Étape — données à importer
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());

  // Étape — fichiers (gardés localement)
  const [files, setFiles] = useState<Record<string, File>>({});

  // Étape — options avancées
  const [config, setConfig] = useState<WizardConfig>(DEFAULT_WIZARD_CONFIG);

  // Étape — correspondances
  const [reconModel, setReconModel] = useState<ReconciliationModel | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [decisions, setDecisions] = useState<ReconciliationDecisions>({});
  // Correspondances Éducation nationale (présentes uniquement si l'école est EN).
  const [enModel, setEnModel] = useState<EnMappingModel | null>(null);
  const [enDecisions, setEnDecisions] = useState<EnMappingDecisions | null>(null);
  // Champs personnalisés (colonnes non natives des fichiers utilisateurs).
  const [cfDecisions, setCfDecisions] = useState<CustomFieldDecisions>({});
  const [cfReport, setCfReport] = useState<CustomFieldsImportReport | null>(null);
  const [cfBusy, setCfBusy] = useState(false);
  const [cfError, setCfError] = useState<string | null>(null);
  const analyzedSigRef = useRef<string>('');

  // Passes post-import : valeurs natives (téléphones/adresses/INE/dates) et
  // jobs asynchrones V2 (valeurs de champs).
  const [importNativeValuesOpt, setImportNativeValuesOpt] = useState(true);
  const [nativeReport, setNativeReport] = useState<NativeValuesImportReport | null>(null);
  const [nativeBusy, setNativeBusy] = useState(false);
  const [nativeError, setNativeError] = useState<string | null>(null);
  const [fvJobs, setFvJobs] = useState<Record<string, FieldValueJobOutput>>({});
  // Progression ligne par ligne des passes paginées (champs perso / natives).
  const [cfProgress, setCfProgress] = useState<{ label: string; done: number; total: number } | null>(null);
  const [nativeProgress, setNativeProgress] = useState<{ label: string; done: number; total: number } | null>(null);

  // Vérification (aperçu upsert calculé côté ADM) + exécution du moteur.
  const [upsertPreview, setUpsertPreview] = useState<UpsertPreview | null>(null);
  const [engineRuns, setEngineRuns] = useState<EngineRun[]>([]);
  const [importDone, setImportDone] = useState(false);

  // Historique + rapport rédigé (IA) de l'import.
  const [historySessionId, setHistorySessionId] = useState<number | null>(null);
  const [reportSession, setReportSession] = useState<ImportSessionSummary | null>(null);
  const [reportBusy, setReportBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Étapes du wizard (la config n'apparaît qu'en mode avancé).
  const steps = useMemo(
    () =>
      [
        { id: 'overview', label: 'École' },
        { id: 'files', label: 'Fichiers' },
        { id: 'data', label: 'À importer' },
        ...(advancedMode ? [{ id: 'config', label: 'Options avancées' }] : []),
        { id: 'reconcile', label: 'Correspondances' },
        { id: 'run', label: 'Import' },
        { id: 'report', label: 'Résultat' },
      ] as Array<{ id: StepId; label: string }>,
    [advancedMode],
  );

  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === currentId),
  );

  // Charge périodes + contenu de l'école au montage.
  useEffect(() => {
    api
      .listSchoolPeriods(code)
      .then(setPeriods)
      .catch((e) =>
        setPeriodsError(
          e?.response?.data?.error ?? 'Impossible de charger les années scolaires de cette école.',
        ),
      );

    setOverviewLoading(true);
    api
      .getTenantOverview(code)
      .then((o) => setOverview(o))
      .catch(() => setOverview(null))
      .finally(() => setOverviewLoading(false));
  }, [code]);

  const orderedSteps = useMemo(
    () => sortStepsByDependencies(Array.from(selectedSteps)),
    [selectedSteps],
  );
  const hasFiles = Object.keys(files).length > 0;
  const fileSig = useMemo(() => fileSignature(files), [files]);

  // Une décision « Créer » à l'étape Correspondances fabrique côté serveur le
  // fichier de l'entité manquante (niveau/classe/matière). Sans le compter ici,
  // l'étape d'import correspondante restait « fichier absent » et n'était jamais
  // jouée : l'utilisateur demandait la création, rien n'était créé, et l'élève
  // ressortait « classe introuvable — non rattaché ».
  const generatedFiles = useMemo(() => {
    const out = new Set<string>();
    for (const [logicalName, byColumn] of Object.entries(decisions)) {
      const columns = reconModel?.files?.[logicalName]?.columns ?? [];
      for (const [column, byValue] of Object.entries(byColumn ?? {})) {
        const dimension = columns.find((c) => c.column === column)?.dimension;
        const file = dimension ? CREATE_FILE_BY_DIMENSION[dimension] : undefined;
        if (!file) continue;
        if (Object.values(byValue ?? {}).some((d) => d?.action === 'create')) out.add(file);
      }
    }
    return out;
  }, [decisions, reconModel]);

  const presentFiles = useMemo(
    () => new Set([...Object.keys(files), ...generatedFiles]),
    [files, generatedFiles],
  );

  // Les CSV issus d'un classeur Excel sont générés avec « ; » : tant qu'il y
  // en a, le délimiteur de la config est ramené à « ; » et verrouillé.
  const hasConvertedWorkbookFiles = useMemo(
    () => Object.values(files).some(isConvertedWorkbookFile),
    [files],
  );
  useEffect(() => {
    if (hasConvertedWorkbookFiles) {
      setConfig((c) => (c.csvDelimiter === ';' ? c : { ...c, csvDelimiter: ';' }));
    }
  }, [hasConvertedWorkbookFiles]);

  // Séparateur réel des fichiers déposés : nos modèles sont en « ; » mais un
  // export Google Sheets est en virgules. Lu avec le mauvais séparateur, un
  // fichier n'a plus qu'UNE colonne (nommée avec l'en-tête entier) et toutes
  // ses colonnes sont déclarées absentes alors qu'elles sont là (école START,
  // 24/08/2026). Aligné à chaque changement de fichiers seulement : un choix
  // manuel fait ensuite dans les options avancées n'est jamais écrasé.
  const [detectedDelimiter, setDetectedDelimiter] = useState<CsvDelimiter | null>(null);
  useEffect(() => {
    if (hasConvertedWorkbookFiles) {
      setDetectedDelimiter(null);
      return;
    }
    const list = Object.values(files);
    if (list.length === 0) {
      setDetectedDelimiter(null);
      return;
    }
    let cancelled = false;
    detectCsvDelimiter(list)
      .then((detected) => {
        if (cancelled || !detected) return;
        setDetectedDelimiter(detected);
        setConfig((c) => (c.csvDelimiter === detected ? c : { ...c, csvDelimiter: detected }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileSig, hasConvertedWorkbookFiles]);

  // Fichiers manquants pour un import cohérent (fermeture des dépendances).
  const missingCoherence = useMemo(
    () => missingCoherenceFiles(selectedSteps, presentFiles),
    [selectedSteps, presentFiles],
  );
  const isCoherent = missingCoherence.length === 0;

  // Proposition automatique : dès que les fichiers changent, on (re)propose les
  // imports dont tous les fichiers (eux + prérequis) sont présents, en tirant
  // leurs dépendances.
  useEffect(() => {
    const present = new Set(Object.keys(files));
    const proposed = new Set(
      STEP_CATALOG.filter(
        (s) => s.category === 'import' && closureFilesSatisfied(s.identifier, present),
      ).map((s) => s.identifier),
    );
    setSelectedSteps(resolveDependencies(proposed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileSig]);

  // Décision « Créer » : l'étape qui créera l'entité doit être jouée, même si
  // l'utilisateur n'a déposé aucun fichier niveaux/classes/matières — c'est la
  // réconciliation qui le fabrique. On AJOUTE seulement (jamais de retrait) pour
  // ne pas écraser un décochage manuel des autres étapes.
  useEffect(() => {
    if (generatedFiles.size === 0) return;
    const implied = Array.from(generatedFiles)
      .map((file) => FILE_TO_STEP[file])
      .filter((step): step is string => Boolean(step));
    if (implied.length === 0) return;
    setSelectedSteps((current) => {
      if (implied.every((step) => current.has(step))) return current;
      return resolveDependencies(new Set([...current, ...implied]));
    });
  }, [generatedFiles]);

  const periodLabel = (p: SchoolPeriod) => p.periodName ?? p.id;

  // Types de période V2 considérés Éducation nationale (SchoolTypeEnum V2).
  const V2_EN_TYPES = ['public', 'private_contract', 'private_non_contract'];
  // Incohérence flag EN : le type de la période V2 pilote la validation
  // matières EN côté V2, le flag ADM pilote le mapping EN côté assistant.
  // S'ils divergent, l'import risque des rejets (école EN non flaguée) ou des
  // codes manquants (école flaguée mais période non EN).
  const selectedPeriod = periods.find((p) => p.id === targetSchoolPeriodId);
  const enMismatch =
    selectedPeriod?.type != null && overview?.school != null
      ? V2_EN_TYPES.includes(String(selectedPeriod.type)) !== overview.school.isEducationNationale
      : false;

  // Travail en cours à protéger : des données ont été saisies et l'import n'est
  // pas terminé.
  const isDirty =
    (selectedSteps.size > 0 || hasFiles || engineRuns.length > 0) &&
    !(currentId === 'report' && importDone);

  // Avertissement au rafraîchissement / fermeture d'onglet.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Avertissement à la navigation interne (quitter l'assistant).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const ok = window.confirm(
      'Quitter l’assistant d’import ? Les données saisies (fichiers, correspondances) seront perdues.',
    );
    if (ok) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  // Analyse à l'entrée de l'étape Correspondances (si fichiers changés).
  useEffect(() => {
    if (currentId !== 'reconcile' || !hasFiles) return;
    const sig = fileSignature(files);
    if (sig === analyzedSigRef.current && reconModel) return;

    setReconLoading(true);
    setActionError(null);
    api
      .analyzeFiles(code, files, config.csvDelimiter)
      .then((model) => {
        if (!mountedRef.current) return;
        setReconModel(model);
        setDecisions(initDecisions(model));
        const en = model.educationNationale ?? null;
        setEnModel(en);
        setEnDecisions(en ? initEnDecisions(en) : null);
        setCfDecisions(model.customFields ? initCfDecisions(model.customFields) : {});
        setCfReport(null);
        setCfError(null);
        analyzedSigRef.current = sig;
      })
      .catch((e) => setActionError(extractError(e, 'Échec de l’analyse des fichiers.')))
      .finally(() => mountedRef.current && setReconLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, hasFiles]);

  // Vérification : aperçu (créations vs déjà présents) + contrôles de
  // cohérence, calculés côté ADM sur les fichiers finaux — aucune écriture.
  // Lancée automatiquement à l'arrivée sur l'étape Import.
  const [previewBusy, setPreviewBusy] = useState(false);
  const runPreview = async () => {
    setPreviewBusy(true);
    setActionError(null);
    setUpsertPreview(null);
    try {
      const upsert = await api.upsertPreview(
        code,
        files,
        decisions,
        config.csvDelimiter,
        enDecisions,
        config.conflictPolicy,
      );
      if (mountedRef.current) setUpsertPreview(upsert);
    } catch (e) {
      setActionError(extractError(e, 'Échec de la vérification des fichiers.'));
    } finally {
      if (mountedRef.current) setPreviewBusy(false);
    }
  };

  // À chaque arrivée sur l'étape Import (hors import déjà lancé) : recalcule
  // la vérification — les décisions ont pu changer à l'étape précédente.
  useEffect(() => {
    if (currentId === 'run' && engineRuns.length === 0 && hasFiles) {
      void runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Sonde les jobs asynchrones V2 (valeurs de champs) jusqu'à un statut
  // terminal — les jobs sont traités par la queue Messenger côté V2.
  const pollFieldValueJobs = async (jobs: FieldValueJobRef[]) => {
    const pending = new Set(jobs.map((j) => j.jobId).filter(Boolean));
    while (pending.size > 0 && mountedRef.current) {
      for (const jobId of Array.from(pending)) {
        try {
          const out = await withRetry(() => api.getCustomFieldJob(code, jobId));
          if (!mountedRef.current) return;
          setFvJobs((prev) => ({ ...prev, [jobId]: out }));
          if (
            out.status === 'completed' ||
            out.status === 'completed_with_errors' ||
            out.status === 'failed'
          ) {
            pending.delete(jobId);
          }
        } catch {
          pending.delete(jobId); // job illisible : on n'insiste pas
        }
      }
      if (pending.size > 0) await sleep(2000);
    }
  };

  // Import réel : le moteur ADM exécute chaque étape sélectionnée dans
  // l'ordre du catalogue, en poussant via les API batch V2 (les fichiers ne
  // sont plus transmis à la V2). Les utilisateurs sont créés INACTIFS, sans
  // envoi d'invitation.
  const runImport = async () => {
    if (orderedSteps.length === 0) return;
    setBusy(true);
    setActionError(null);
    setImportDone(false);
    const runs: EngineRun[] = orderedSteps.map((stepId) => ({
      stepId,
      name: STEP_BY_ID[stepId]?.name ?? stepId,
      status: 'pending',
    }));
    setEngineRuns(runs);

    const patchRun = (stepId: string, patch: Partial<EngineRun>) => {
      setEngineRuns((prev) => prev.map((r) => (r.stepId === stepId ? { ...r, ...patch } : r)));
    };

    // Historique : ouvre une session d'import (qui/quand/année cible) et y
    // archive les fichiers sources — chaque élément poussé y sera tracé.
    // Best-effort : un échec d'historisation n'empêche pas l'import.
    let sessionId: number | null = null;
    try {
      sessionId = await createImportSession(code, files, {
        targetSchoolPeriodId,
        targetSchoolPeriodLabel: selectedPeriod ? periodLabel(selectedPeriod) : undefined,
      });
      setHistorySessionId(sessionId);
    } catch (e) {
      console.error("Historique d'import indisponible :", e);
    }

    // Étapes découpables : envoyées par pages de lignes → requêtes courtes et
    // barre de progression ligne par ligne (import_subjects fusionne ses
    // lignes par matière : une seule requête).
    const PAGE_SIZE = 100;
    const sliceable = new Set([
      'import_levels',
      'import_teachers',
      'import_directors',
      'import_parents',
      'import_classes',
      'import_students',
      'link_guardians',
    ]);
    const mergeReports = (into: EngineStepReport | undefined, page: EngineStepReport): EngineStepReport =>
      into === undefined
        ? page
        : {
            ...page,
            total: into.total + page.total,
            created: into.created + page.created,
            updated: into.updated + page.updated,
            skipped: into.skipped + page.skipped,
            failed: into.failed + page.failed,
            errors: [...into.errors, ...page.errors].slice(0, 20),
            warnings: [...into.warnings, ...page.warnings].slice(0, 20),
            processed: into.processed + page.processed,
          };

    try {
      let hardFailure = false;
      for (const stepId of orderedSteps) {
        patchRun(stepId, { status: 'running' });
        try {
          let stepReport: EngineStepReport | undefined;
          if (sliceable.has(stepId)) {
            let offset = 0;
            for (;;) {
              const page = await withRetry(() =>
                api.runEngineStep(
                  code,
                  stepId,
                  files,
                  config.csvDelimiter,
                  decisions,
                  enDecisions,
                  { offset, limit: PAGE_SIZE },
                  sessionId,
                ),
              );
              if (!mountedRef.current) return;
              stepReport = mergeReports(stepReport, page);
              offset += PAGE_SIZE;
              const done = Math.min(offset, page.totalRows);
              patchRun(stepId, {
                report: stepReport,
                progress: { done, total: page.totalRows },
              });
              if (offset >= page.totalRows) break;
            }
          } else {
            stepReport = await withRetry(() =>
              api.runEngineStep(
                code,
                stepId,
                files,
                config.csvDelimiter,
                decisions,
                enDecisions,
                undefined,
                sessionId,
              ),
            );
          }
          if (!mountedRef.current) return;
          patchRun(stepId, {
            status: (stepReport?.failed ?? 0) > 0 ? 'failed' : 'done',
            report: stepReport,
            progress: undefined,
          });
        } catch (e) {
          if (!mountedRef.current) return;
          hardFailure = true;
          patchRun(stepId, {
            status: 'failed',
            error: extractError(e, 'Étape en échec.'),
            progress: undefined,
          });
        }
      }

      const pendingJobs: FieldValueJobRef[] = [];

      // Champs personnalisés : après la création des utilisateurs (matching
      // par email, valeurs poussées via le batch asynchrone V2). Paginé par
      // fichier / pages de lignes → progression et requêtes courtes.
      if (hasCfImports(cfDecisions)) {
        setCfBusy(true);
        setCfError(null);
        // Un fichier en échec (après retries) n'empêche pas les suivants :
        // l'erreur est localisée (fichier + ligne) et les valeurs déjà
        // envoyées sont conservées (upsert).
        const cfErrors: string[] = [];
        let merged: CustomFieldsImportReport | null = null;
        const cfFiles = Object.entries(cfDecisions)
          .filter(
            ([logicalName, cols]) =>
              files[logicalName] && Object.values(cols).some((d) => d.action === 'import'),
          )
          .map(([logicalName]) => logicalName);
        for (const logicalName of cfFiles) {
          let offset = 0;
          try {
            for (;;) {
              const page = await withRetry(() =>
                api.importCustomFields(
                  code,
                  files,
                  cfDecisions,
                  config.csvDelimiter,
                  targetSchoolPeriodId,
                  { file: logicalName, offset, limit: POST_PAGE_SIZE },
                ),
              );
              if (!mountedRef.current) return;
              merged = mergeCfReports(merged, page);
              setCfReport(merged);
              pendingJobs.push(...(page.jobs ?? []));
              offset += POST_PAGE_SIZE;
              const total = page.totalRows ?? 0;
              setCfProgress({
                label: FILE_LABELS[logicalName] ?? logicalName,
                done: Math.min(offset, total),
                total,
              });
              if (offset >= total) break;
            }
          } catch (e) {
            if (!mountedRef.current) return;
            cfErrors.push(
              `${FILE_LABELS[logicalName] ?? logicalName} : interrompu à la ligne ${offset} — ${extractError(e, 'erreur inconnue')}`,
            );
          }
        }
        if (mountedRef.current) {
          if (cfErrors.length > 0) {
            setCfError(
              `Import des champs personnalisés incomplet. ${cfErrors.join(' ; ')}. Relancez l’import pour compléter (les valeurs déjà envoyées seront reconnues).`,
            );
          }
          setCfBusy(false);
          setCfProgress(null);
        }
      }

      // Téléphones, adresses, INE, nationalité, profession, dates de
      // scolarité : rattachés aux champs natifs des fiches. Paginé (le
      // géocodage des adresses se fait ligne à ligne).
      if (importNativeValuesOpt) {
        setNativeBusy(true);
        setNativeError(null);
        const nativeErrors: string[] = [];
        let merged: NativeValuesImportReport | null = null;
        const nativeFiles = ['eleves', 'parents', 'enseignants', 'direction'].filter(
          (logicalName) => files[logicalName],
        );
        for (const logicalName of nativeFiles) {
          let offset = 0;
          try {
            for (;;) {
              const page = await withRetry(() =>
                api.importNativeValues(
                  code,
                  files,
                  config.csvDelimiter,
                  targetSchoolPeriodId,
                  { file: logicalName, offset, limit: NATIVE_PAGE_SIZE },
                ),
              );
              if (!mountedRef.current) return;
              merged = mergeNativeReports(merged, page);
              setNativeReport(merged);
              pendingJobs.push(...(page.jobs ?? []));
              offset += NATIVE_PAGE_SIZE;
              const total = page.totalRows ?? 0;
              setNativeProgress({
                label: FILE_LABELS[logicalName] ?? logicalName,
                done: Math.min(offset, total),
                total,
              });
              if (offset >= total) break;
            }
          } catch (e) {
            if (!mountedRef.current) return;
            nativeErrors.push(
              `${FILE_LABELS[logicalName] ?? logicalName} : interrompu à la ligne ${offset} — ${extractError(e, 'erreur inconnue')}`,
            );
          }
        }
        if (mountedRef.current) {
          if (nativeErrors.length > 0) {
            setNativeError(
              `Reprise des téléphones/adresses incomplète. ${nativeErrors.join(' ; ')}. Relancez l’import pour compléter (les valeurs déjà envoyées seront reconnues).`,
            );
          }
          setNativeBusy(false);
          setNativeProgress(null);
        }
      }

      // Clôture de la session d'historique (statut final calculé côté
      // serveur : « avec erreurs » si des éléments ont échoué), puis
      // génération du rapport rédigé (IA) en arrière-plan : l'écran
      // Résultat l'affiche dès qu'il est prêt.
      if (sessionId !== null) {
        try {
          await finishImportSession(code, sessionId, hardFailure ? 'failed' : 'completed');
        } catch (e) {
          console.error("Clôture de l'historique d'import impossible :", e);
        }
        const reportSessionId = sessionId;
        setReportBusy(true);
        void generateImportReport(code, reportSessionId)
          .then((s) => {
            if (mountedRef.current) setReportSession(s);
          })
          .catch((e) => console.error("Génération du rapport d'import impossible :", e))
          .finally(() => {
            if (mountedRef.current) setReportBusy(false);
          });
      }

      if (mountedRef.current) {
        setImportDone(!hardFailure);
        setCurrentId('report');
      }

      // Suivi des jobs asynchrones V2 (non bloquant pour l'affichage du
      // rapport : l'étape résultat se met à jour au fil de l'eau).
      if (pendingJobs.length > 0) {
        void pollFieldValueJobs(pendingJobs);
      }
    } catch (e) {
      setActionError(extractError(e, 'Échec de l’import.'));
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const canAdvance = (): boolean => {
    switch (currentId) {
      case 'overview':
        return Boolean(targetSchoolPeriodId);
      case 'files':
        return hasFiles;
      case 'data':
        return selectedSteps.size > 0 && isCoherent;
      case 'config':
        return true;
      case 'reconcile':
        return !reconLoading;
      default:
        return true;
    }
  };

  const goTo = (index: number) => {
    const clamped = Math.min(Math.max(index, 0), steps.length - 1);
    setCurrentId(steps[clamped].id);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-16">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Assistant d’import de données</h1>
          <p className="text-sm text-muted-foreground">
            École <span className="font-mono">{code}</span> — import depuis des fichiers
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={advancedMode} onCheckedChange={setAdvancedMode} />
            Mode avancé
          </label>
          <Button variant="ghost" onClick={() => navigate(`/schools/${code}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la fiche
          </Button>
        </div>
      </div>

      {periodsError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{periodsError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="py-6">
          <Stepper
            steps={steps}
            currentStep={currentIndex}
            onStepClick={(i) => i <= currentIndex && goTo(i)}
          />
        </CardContent>
      </Card>

      {actionError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <StepperContent>
        {/* Contenu de l'école & année scolaire */}
        {currentId === 'overview' && (
          <div className="space-y-6">
            <TenantOverview overview={overview} loading={overviewLoading} />
            <Card>
              <CardHeader icon={<GraduationCap />} iconColor="blue">
                <CardTitle className="text-base">Année scolaire cible</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Les données des fichiers seront importées dans l’année scolaire sélectionnée.
                </p>
                <div className="space-y-1.5 sm:max-w-md">
                  <Label className="text-sm">Année scolaire</Label>
                  <Select value={targetSchoolPeriodId} onValueChange={setTargetSchoolPeriodId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez une année scolaire…" />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {periodLabel(p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {enMismatch && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Incohérence Éducation nationale : la période V2 sélectionnée est de type «{' '}
                      {String(selectedPeriod?.type)} »{' '}
                      {overview?.school?.isEducationNationale
                        ? "alors que l'école est marquée Éducation nationale côté ADM — les codes officiels (MEF/BCN) ne seront pas exploités par la V2."
                        : "(Éducation nationale côté V2) alors que l'école n'est pas marquée EN côté ADM — les correspondances EN ne seront pas proposées et l'import de matières risque des rejets."}{' '}
                      Corrigez le flag de l'école (fiche ADM) ou le type de la période V2 avant
                      d'importer.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Fichiers */}
        {currentId === 'files' && (
          <FileUploader
            code={code}
            requiredFiles={ALL_LOGICAL_FILES}
            files={files}
            onFilesChange={setFiles}
            detectedDelimiter={detectedDelimiter}
          />
        )}

        {/* À importer */}
        {currentId === 'data' && (
          <StepSelector
            selected={selectedSteps}
            onChange={setSelectedSteps}
            presentFiles={presentFiles}
            advanced={advancedMode}
          />
        )}

        {/* Options avancées (mode avancé uniquement) */}
        {currentId === 'config' && (
          <ConfigForm
            config={config}
            onChange={setConfig}
            delimiterLocked={hasConvertedWorkbookFiles}
            detectedDelimiter={detectedDelimiter}
          />
        )}

        {/* Correspondances */}
        {currentId === 'reconcile' && (
          <div className="space-y-6">
            {enModel && enDecisions && !reconLoading && (
              <EducationNationaleMapping
                code={code}
                model={enModel}
                decisions={enDecisions}
                onChange={(updater) => setEnDecisions((prev) => (prev ? updater(prev) : prev))}
              />
            )}
            <ReconciliationStep
              code={code}
              model={hasFiles ? reconModel : { files: {} }}
              loading={reconLoading}
              decisions={decisions}
              onChange={setDecisions}
            />
            {reconModel?.customFields && !reconLoading && (
              <CustomFieldsMapping
                code={code}
                model={reconModel.customFields}
                decisions={cfDecisions}
                onChange={(updater) => setCfDecisions((prev) => updater(prev))}
              />
            )}
          </div>
        )}

        {/* Import (vérification intégrée) */}
        {currentId === 'run' && (
          <div className="space-y-6">
            <Alert>
              <Upload className="h-4 w-4" />
              <AlertDescription>
                Vérifiez le récapitulatif ci-dessous puis lancez l’import. Les utilisateurs sont
                créés <strong>inactifs</strong> : aucune invitation ne part avant l’activation
                volontaire des comptes.
              </AlertDescription>
            </Alert>

            {engineRuns.length === 0 && (
              <>
                {previewBusy && (
                  <p className="flex items-center text-sm text-muted-foreground">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Vérification de vos fichiers (comparaison avec l’école, contrôles de
                    cohérence)…
                  </p>
                )}
                {upsertPreview && !previewBusy && (
                  <>
                    <UpsertSection upsert={upsertPreview} />
                    <AnomaliesPanel anomalies={upsertPreview.anomalies ?? []} />
                  </>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Après l’import</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <label className="flex cursor-pointer items-start gap-3 text-sm">
                      <Switch
                        checked={importNativeValuesOpt}
                        onCheckedChange={setImportNativeValuesOpt}
                      />
                      <span>
                        <span className="font-medium">
                          Reprendre téléphones, adresses, INE et dates de scolarité
                        </span>{' '}
                        <span className="text-muted-foreground">
                          — ces colonnes sont rattachées aux champs natifs des fiches (adresses
                          géocodées).
                        </span>
                      </span>
                    </label>
                  </CardContent>
                </Card>
              </>
            )}
            {engineRuns.length > 0 && (
              <EngineStepsPanel runs={engineRuns} title="Import en cours" />
            )}
            {(cfBusy || nativeBusy) && (
              <Card>
                <CardContent className="space-y-3 pt-6">
                  {cfBusy && (
                    <div className="space-y-1.5">
                      <p className="flex items-center text-sm text-muted-foreground">
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Import des champs personnalisés
                        {cfProgress ? ` — ${cfProgress.label}` : ''}…
                      </p>
                      {cfProgress && cfProgress.total > 0 && (
                        <div className="flex items-center gap-3 pl-6">
                          <Progress
                            value={Math.round((cfProgress.done / cfProgress.total) * 100)}
                            className="h-2 flex-1"
                          />
                          <span className="whitespace-nowrap text-xs text-muted-foreground">
                            {cfProgress.done}/{cfProgress.total} lignes
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {nativeBusy && (
                    <div className="space-y-1.5">
                      <p className="flex items-center text-sm text-muted-foreground">
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Reprise des téléphones, adresses et dates
                        {nativeProgress ? ` — ${nativeProgress.label}` : ''}…
                      </p>
                      {nativeProgress && nativeProgress.total > 0 && (
                        <div className="flex items-center gap-3 pl-6">
                          <Progress
                            value={Math.round((nativeProgress.done / nativeProgress.total) * 100)}
                            className="h-2 flex-1"
                          />
                          <span className="whitespace-nowrap text-xs text-muted-foreground">
                            {nativeProgress.done}/{nativeProgress.total} lignes
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Résultat */}
        {currentId === 'report' && (
          <div className="space-y-6">
            {/* Rapport complet rédigé (IA) — généré automatiquement à la fin
                de l'import, consultable ensuite depuis la fiche école. */}
            {reportBusy && !reportSession?.aiAnalysis && (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-10 text-center">
                <Sparkles className="mb-3 h-8 w-8 animate-pulse text-primary" />
                <p className="font-medium">Analyse de l’import en cours…</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  L’intelligence artificielle rédige le rapport à partir des chiffres exacts de
                  l’import. Cela prend quelques secondes.
                </p>
              </div>
            )}
            {reportSession?.aiAnalysis && historySessionId !== null && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold">Rapport de l’import</h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/schools/${code}/imports/${historySessionId}`)}
                    >
                      <ExternalLink className="mr-1 h-4 w-4" />
                      Détail de l’import
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        window.open(
                          `/schools/${code}/imports/${historySessionId}/report?print=1`,
                          '_blank',
                        )
                      }
                    >
                      <FileDown className="mr-1 h-4 w-4" />
                      Télécharger en PDF
                    </Button>
                  </div>
                </div>
                <ImportReportView session={reportSession} />
              </div>
            )}
            <EngineStepsPanel runs={engineRuns} title="Résultat de l’import" />
            {cfError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{cfError}</AlertDescription>
              </Alert>
            )}
            {cfReport && <CustomFieldsReport report={cfReport} />}
            {nativeError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{nativeError}</AlertDescription>
              </Alert>
            )}
            {nativeReport && <NativeValuesReport report={nativeReport} />}
            {Object.keys(fvJobs).length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <FieldValueJobsSummary
                    jobs={fvJobs}
                    emails={{
                      ...(cfReport?.emailsByUserRole ?? {}),
                      ...(nativeReport?.emailsByUserRole ?? {}),
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </StepperContent>

      <StepperActions>
        <Button variant="outline" onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0 || busy}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Précédent
        </Button>

        <div className="flex gap-2">
          {currentId === 'run' ? (
            <Button onClick={runImport} disabled={busy || previewBusy || orderedSteps.length === 0}>
              <Play className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
              Lancer l’import
            </Button>
          ) : currentId === 'report' ? (
            <Button onClick={() => navigate(`/schools/${code}`)}>Terminer</Button>
          ) : (
            <Button onClick={() => goTo(currentIndex + 1)} disabled={!canAdvance() || busy}>
              Suivant
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </StepperActions>
    </div>
  );
}

/** Fusionne les pages du rapport champs perso (compteurs par colonne). */
function mergeCfReports(
  into: CustomFieldsImportReport | null,
  page: CustomFieldsImportReport,
): CustomFieldsImportReport {
  if (!into) return page;
  const files = { ...into.files };
  for (const [logicalName, pageFile] of Object.entries(page.files)) {
    const existing = files[logicalName];
    if (!existing) {
      files[logicalName] = pageFile;
      continue;
    }
    const columns = existing.columns.map((col) => {
      const pageCol = pageFile.columns.find((c) => c.column === col.column);
      if (!pageCol) return col;
      return {
        ...col,
        attribute: col.attribute === 'error' ? col.attribute : pageCol.attribute === 'created' ? 'created' as const : col.attribute,
        created: col.created + pageCol.created,
        reused: col.reused + pageCol.reused,
        queued: (col.queued ?? 0) + (pageCol.queued ?? 0),
        skipped: col.skipped + pageCol.skipped,
        errors: [...col.errors, ...pageCol.errors].slice(0, 15),
      };
    });
    for (const pageCol of pageFile.columns) {
      if (!columns.some((c) => c.column === pageCol.column)) columns.push(pageCol);
    }
    files[logicalName] = {
      ...existing,
      columns,
      usersNotFound: existing.usersNotFound + pageFile.usersNotFound,
    };
  }
  return {
    files,
    warnings: [...into.warnings, ...page.warnings].slice(0, 15),
    jobs: [...(into.jobs ?? []), ...(page.jobs ?? [])],
    emailsByUserRole: { ...(into.emailsByUserRole ?? {}), ...(page.emailsByUserRole ?? {}) },
  };
}

/** Fusionne les pages du rapport valeurs natives. */
function mergeNativeReports(
  into: NativeValuesImportReport | null,
  page: NativeValuesImportReport,
): NativeValuesImportReport {
  if (!into) return page;
  const files = { ...into.files };
  for (const [logicalName, pageFile] of Object.entries(page.files)) {
    const existing = files[logicalName];
    if (!existing) {
      files[logicalName] = pageFile;
      continue;
    }
    const columns = existing.columns.map((col) => {
      const pageCol = pageFile.columns.find((c) => c.column === col.column);
      if (!pageCol) return col;
      return {
        ...col,
        queued: (col.queued ?? 0) + (pageCol.queued ?? 0),
        created: col.created + pageCol.created,
        reused: col.reused + pageCol.reused,
        skipped: col.skipped + pageCol.skipped,
        errors: [...col.errors, ...pageCol.errors].slice(0, 15),
      };
    });
    for (const pageCol of pageFile.columns) {
      if (!columns.some((c) => c.column === pageCol.column)) columns.push(pageCol);
    }
    const sumStats = <T extends { created: number; reused: number; skipped: number; errors: string[] }>(
      a: T | undefined,
      b: T | undefined,
    ): T | undefined =>
      a && b
        ? {
            ...a,
            ...Object.fromEntries(
              Object.entries(a)
                .filter(([, v]) => typeof v === 'number')
                .map(([k, v]) => [k, (v as number) + ((b as Record<string, unknown>)[k] as number ?? 0)]),
            ),
            errors: [...a.errors, ...b.errors].slice(0, 15),
          }
        : (a ?? b);
    files[logicalName] = {
      ...existing,
      columns,
      address: sumStats(existing.address, pageFile.address),
      dates:
        existing.dates && pageFile.dates
          ? {
              updated: existing.dates.updated + pageFile.dates.updated,
              reused: existing.dates.reused + pageFile.dates.reused,
              skipped: existing.dates.skipped + pageFile.dates.skipped,
              errors: [...existing.dates.errors, ...pageFile.dates.errors].slice(0, 15),
            }
          : (existing.dates ?? pageFile.dates),
      usersNotFound: existing.usersNotFound + pageFile.usersNotFound,
    };
  }
  return {
    files,
    warnings: [...into.warnings, ...page.warnings].slice(0, 15),
    jobs: [...(into.jobs ?? []), ...(page.jobs ?? [])],
    emailsByUserRole: { ...(into.emailsByUserRole ?? {}), ...(page.emailsByUserRole ?? {}) },
  };
}

function extractError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e) && !e.response) {
    return 'connexion interrompue (le serveur n’a pas répondu : requête trop longue ou coupure réseau), malgré plusieurs tentatives';
  }
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: string; details?: string } } }).response;
    return resp?.data?.details ?? resp?.data?.error ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}
